import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router';

import {
  Hospital,
  HospitalStudies,
  Study,
} from '../../../../shared/api/hscan/hospital';
import {
  StudiesPrice,
  useStudiesFromHospital,
} from '../../../../shared/hooks/hospitalHook';
import { useServerSideSelected } from '../../common/components/SortableTable';
import { useAutoSearchHospital } from '../../hooks/autoSearchHospital';

import { PaymentSection } from './PaymentSection';
import { SearchHospitalSection } from './SearchHospitalSection';
import { SelectImageSection } from './SelectImageSection';
import { SendImageSection } from './SendImageSection';

export const RequestImages = () => {
  const [hospital, setHospital] = useState<Hospital | null>(null);
  const [rrNumber, setRrNumber] = useState<string>('');
  const [studies, setStudies] = useState<Study[]>([]);
  /* 병원 별 study 정보를 담고 있음 */
  const [hospitalStudies, setHospitalStudies] = useState<HospitalStudies>({});
  const [destGateway, setDestGateway] = useState<Hospital | null>(null);
  const [allowSend, setAllowSend] = useState(false);
  const [checkAllTerms, setCheckAllTerms] = useState(false);
  const [pricePerHospital, setPricePerHospital] = useState<
    Record<string, StudiesPrice>
  >({});
  const isRequestFormValid =
    checkAllTerms && (destGateway === null || allowSend);
  const { search } = useLocation();
  const queryParams = useMemo(() => new URLSearchParams(search), [search]);
  const hospitalIdQueryParam = queryParams.get('hospitalId');

  const {
    isFetchedAfterMount: isFetchedAfterMountStudies,
    refetch: queryStudies,
    isFetching: isFetchingStudies,
  } = useStudiesFromHospital(
    {
      id: hospital?.id ?? '',
      registration: rrNumber,
    },
    {
      enabled: false,
    },
  );

  const { selected, selectOne, selectAll, unselectAll } = useServerSideSelected(
    studies,
    'studyInstanceUID',
  );

  // 검색한 병원의 선택한 케이스 갱신
  useEffect(() => {
    if (hospital?.id && isFetchedAfterMountStudies) {
      setHospitalStudies(prev => ({
        ...prev,
        [hospital.id]: studies.filter(study =>
          selected.includes(study.studyInstanceUID),
        ),
      }));
    }
  }, [hospital?.id, isFetchedAfterMountStudies, selected, studies]);

  useEffect(() => {
    if (hospital?.id) {
      unselectAll();
    }
  }, [hospital?.id, unselectAll]);

  const initializePage = () => {
    setHospital(null);
    setRrNumber('');
    setStudies([]);
    setHospitalStudies({});
    unselectAll();
  };

  const searchStudies = async () => {
    unselectAll();
    try {
      const { data } = await queryStudies();
      if (data && hospital) {
        setStudies(data);
      }
    } catch {
      setStudies([]);
    }
  };

  const { abortAutoSearch } = useAutoSearchHospital({
    hospitalId: hospitalIdQueryParam ?? '',
    onRequestHospitalSuccess: setHospital,
    isSearchable: hospital !== null,
    search: searchStudies,
  });

  const selectedHospitalIds = useMemo(
    () => Object.keys(pricePerHospital),
    [pricePerHospital],
  );

  return (
    <>
      <SearchHospitalSection
        hospital={hospital}
        rrNumber={rrNumber}
        onChangeHospital={newHospital => {
          setHospital(newHospital);
          setStudies([]);
          setRrNumber('');
        }}
        onChangeRrNumber={rrn => setRrNumber(rrn)}
        onSearchImage={searchStudies}
        onChangeQuery={abortAutoSearch}
      />
      <SelectImageSection
        isLoadingQueriedStudies={isFetchingStudies}
        searched={hospital !== null && isFetchedAfterMountStudies}
        studies={studies}
        selected={selected}
        selectOne={selectOne}
        selectAll={selectAll}
        unselectAll={unselectAll}
      />
      <SendImageSection
        hospital={destGateway}
        onChangeHospital={setDestGateway}
        checkTerm={allowSend}
        setCheckTerm={setAllowSend}
        selectedHospitalIds={selectedHospitalIds}
      />
      <PaymentSection
        searched={isFetchedAfterMountStudies}
        onSuccessPayment={initializePage}
        hospital={hospital}
        hospitalStudies={hospitalStudies}
        rrNumber={rrNumber}
        destGatewayId={destGateway?.id}
        checkTerm={checkAllTerms}
        setCheckTerm={setCheckAllTerms}
        pricePerHospital={pricePerHospital}
        setPricePerHospital={setPricePerHospital}
        isRequestFormValid={isRequestFormValid}
      />
    </>
  );
};
